import { usePublishedHero } from 'hooks/usePublishedHero';
import PageStoryImage from 'components/PageStoryImage';
import HeroBackground from 'components/HeroBackground';
import HeroScrollRail from 'components/HeroScrollRail';
import PageIntroBand from 'components/PageIntroBand';
import PartnerApplication from 'components/partners/PartnerApplication';
import { FlowButton } from 'components/ui/flow-button';
import SiteHeroCtas, { PAGE_HERO_CONTAINER_CLASS, PAGE_HERO_EYEBROW_CLASS, PAGE_HERO_H1_CLASS, PAGE_HERO_BODY_CLASS } from 'components/SiteHeroCtas';
import './PartnerPage.css';
import { useState } from 'react';
import { useReveal } from 'hooks/useReveal';
const PARTNER_MARQUEE_ITEMS = [
  'Educators and course speakers',
  'Vendors and service providers',
  'Referral and affiliate partners',
  'Resellers',
  'Recorded courses',
  'Live sessions',
  'TMS placements',
  'Paperwork packages',
];
const PARTNER_PATHS = [
  {
    "id": "speaker",
    "title": "Educators and course speakers",
    "lede": "Share your knowledge through recorded courses, live sessions or both.",
    "paragraphs": [
      "Teach safety, ELD, dispatch, accounting, maintenance or another part of running a trucking company. Magellan provides course hosting, promotion and learner administration.",
      "You earn when a learner purchases your video or books your live session, under the payment terms agreed in your offer. Tell us what you can teach and what learners should be able to do afterward."
    ],
    "cta": "Propose a course or session"
  },
  {
    "id": "vendor",
    "title": "Vendors and service providers",
    "lede": "Present your products and services to carriers through CarrierFlow TMS.",
    "paragraphs": [
      "Paid placements can promote products such as tires and services including insurance, factoring and ELD support. Law firms and other service providers can also discuss ways for carriers to access their support within CarrierFlow.",
      "Tell us what you offer and how it helps carriers. We prepare a tailored price and scope for the placement or service arrangement."
    ],
    "cta": "Propose a product or service"
  },
  {
    "id": "referral",
    "title": "Referral and affiliate partners",
    "lede": "Earn an agreed fee per referral.",
    "paragraphs": [
      "Introduce customers to paperwork packages, courses, learning management system (LMS) access and CarrierFlow TMS. Tell us who you work with and which offerings fit their needs.",
      "Customers pay Magellan directly. We pay your referral fee once their payment is confirmed, under the terms agreed in your offer."
    ],
    "cta": "Discuss referrals"
  },
  {
    "id": "reseller",
    "title": "Resellers",
    "lede": "Earn an agreed percentage of the sales you make.",
    "paragraphs": [
      "Sell Magellan’s paperwork packages, courses, LMS access and CarrierFlow TMS to the customers you serve. Customers pay Magellan directly, and we pay your agreed percentage after their payment is confirmed. Tell us about your market and the offerings you want to sell."
    ],
    "cta": "Discuss reselling"
  }
];

export default function PartnerPage() {
  const heroMedia = usePublishedHero('partnerHero');
  const ref = useReveal();
  const [partnerType, setPartnerType] = useState('referral');
  return (
    <main ref={ref} className="min-h-screen bg-canvas partner-page">
      <section data-theme-surface="dark" data-testid="partner-hero" className="relative flex min-h-[100svh] w-full items-center overflow-hidden md:h-screen md:min-h-[680px] site-image-hero">
        <HeroBackground imageUrl={heroMedia.imageUrl} imageAlt={heroMedia.imageAlt} />
        <div className="absolute inset-0 grain-overlay" />
        <div data-hero-text-parallax="true" className={PAGE_HERO_CONTAINER_CLASS}>
          <div className="max-w-4xl">
            <p className={PAGE_HERO_EYEBROW_CLASS}>Partner with Magellan International</p>
            <h1 className={PAGE_HERO_H1_CLASS}>Bring your expertise to the<br className="hidden lg:block" /> <em className="shimmer-text">work behind trucking.</em></h1>
            <div className={PAGE_HERO_BODY_CLASS}>
              <p>Teach skills for US trucking, present your products or services, refer customers or sell Magellan’s paperwork, courses and software.</p>
              <p>Partnerships are open to individuals and companies.</p>
            </div>
            <SiteHeroCtas content={{ primaryCtaLabel: 'Propose a partnership', primaryCtaPath: '#partner-application', secondaryCtaLabel: 'Explore partner types', secondaryCtaPath: '#partner-types' }} />
          </div>
        </div>
        <HeroScrollRail />
      </section>

      <div className="topic-rail overflow-hidden border-y border-ink/10 bg-surface">
        <div className="marquee-container">
          <div className="marquee-track">
            {[...PARTNER_MARQUEE_ITEMS, ...PARTNER_MARQUEE_ITEMS].map((item, index) => (
              <span key={`${item}-${index}`} aria-hidden={index >= PARTNER_MARQUEE_ITEMS.length ? true : undefined} className="inline-flex items-center gap-3 px-9 font-body text-[9px] uppercase tracking-[0.44em] text-copy/24 whitespace-nowrap">
                {item}
                <span className="h-1 w-1 rotate-45 bg-gold/45" />
              </span>
            ))}
          </div>
        </div>
      </div>

      <PageIntroBand testId="partner-page-intro" label="Why partner with Magellan" title="Specialist knowledge matters." backgroundText="PARTNERS" paragraphs={[
        'Running a trucking company takes more than one kind of expertise.',
        'Magellan brings together practical documentation, education and software for US carriers. Partnerships give educators and businesses a way to contribute their knowledge, products and services to that work.',
      ]} />

      <section id="partner-types" data-testid="partner-types" className="partner-section">
        <div className="partner-shell">
          <p className="partner-label">01 / Ways to work together</p>
          <div className="partner-section-heading"><h2>Four ways to partner with Magellan.</h2><div><p>Educators earn from course purchases or live-session bookings. Vendors receive a tailored proposal for paid placements or service arrangements.</p><p>Referral partners make introductions for an agreed fee; resellers earn an agreed percentage of the sales they make.</p></div></div>
          <PageStoryImage testId="partner-paths-image" src="/images/careers/desk-headset.jpg" alt="A laptop, headset and planner arranged for office work" position="center 62%" />
          <div className="partner-paths">
            {PARTNER_PATHS.map((path,index) => <article className="partner-path" key={path.id} data-testid={`partner-path-${path.id}`}>
              <div className="partner-path-heading"><span className="partner-label">0{index+1}</span><h3>{path.title}</h3><p>{path.lede}</p></div>
              <div className="partner-path-copy">{path.paragraphs.map(paragraph=><p key={paragraph}>{paragraph}</p>)}<FlowButton as="a" href="#partner-application" onClick={()=>setPartnerType(path.id)}>{path.cta}</FlowButton></div>
            </article>)}
          </div>
        </div>
      </section>

      <section data-testid="partner-proposal-guidance" className="partner-section partner-section-tint">
        <div className="partner-shell">
          <p className="partner-label">02 / Your proposal</p>
          <h2>What to include in your proposal.</h2>
          <PageStoryImage testId="partner-proposal-image" src="/images/careers/document-review.jpg" alt="Reviewing written documents and preparing notes" position="center 63%" />
          <div className="partner-proposal-points">
            <div><span className="partner-label">Your experience</span><p>Tell us about your work, the people you serve and any relevant examples you can share.</p></div>
            <div><span className="partner-label">What you offer</span><p>Explain what you want to teach, offer, refer or resell, and who would benefit.</p></div>
            <div><span className="partner-label">Support you need</span><p>Describe what you would handle directly and what support, access or materials you would need from Magellan.</p></div>
          </div>
          <p className="partner-principle">These details give us a starting point for the conversation. We work out the terms together.</p>
        </div>
      </section>

      <section id="partner-application" data-testid="partner-application" className="partner-section">
        <div className="partner-shell partner-application-shell">
          <p className="partner-label">03 / Partner application</p>
          <h2>Start a partnership conversation.</h2>
          <p className="partner-application-intro">Choose your partner type and tell us what you have in mind.</p>
          <p className="partner-application-helper">We arrange a meeting to discuss your proposal, then send an offer covering the scope, responsibilities and commercial terms. Once you accept, we begin the agreed partnership.</p>
          <PartnerApplication partnerType={partnerType} onTypeChange={setPartnerType} />
        </div>
      </section>
    </main>
  );
}
